import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Tag, Plus, Pencil, Trash2, RefreshCw, Loader2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import adminService from '../../services/adminService';

const EMPTY_FORM = { parentCategoryId: '', name: '', description: '', isActive: true };

// Flatten category tree with depth info for hierarchical select
function flattenCategories(categories, depth = 0, parentId = null) {
  const flat = [];
  const children = categories.filter(c => (c.parentCategoryId ?? null) === parentId);
  for (const cat of children) {
    flat.push({ ...cat, depth });
    flat.push(...flattenCategories(categories, depth + 1, cat.productCategoryId));
  }
  // Include items without matched parent at root level (shouldn't happen but safety)
  if (depth === 0) {
    const attached = new Set(flat.map(c => c.productCategoryId));
    for (const cat of categories) {
      if (!attached.has(cat.productCategoryId)) flat.push({ ...cat, depth: 0 });
    }
  }
  return flat;
}

export default function AdminProductCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editCat, setEditCat] = useState(null); // null = create, else = edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  const fetchCategories = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await adminService.getAdminProductCategories();
      setCategories(Array.isArray(result) ? result : result?.data ?? []);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to load categories');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openCreate = () => {
    setEditCat(null);
    setForm(EMPTY_FORM);
    setIsFormOpen(true);
  };

  const openEdit = (cat) => {
    setEditCat(cat);
    setForm({
      parentCategoryId: cat.parentCategoryId ? String(cat.parentCategoryId) : '',
      name: cat.name,
      description: cat.description ?? '',
      isActive: cat.isActive,
    });
    setIsFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setIsSaving(true);
    try {
      const dto = {
        parentCategoryId: form.parentCategoryId ? parseInt(form.parentCategoryId) : null,
        name: form.name.trim(),
        description: form.description || null,
        isActive: form.isActive,
      };
      if (editCat) {
        await adminService.updateAdminProductCategory(editCat.productCategoryId, dto);
        toast.success('Category updated');
      } else {
        await adminService.createAdminProductCategory(dto);
        toast.success('Category created');
      }
      setIsFormOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to save category');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? This cannot be undone.`)) return;
    try {
      await adminService.deleteAdminProductCategory(cat.productCategoryId);
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to delete category');
    }
  };

  // Build flat list with depth for the parent selector (exclude current when editing)
  const flatForSelect = flattenCategories(categories).filter(c =>
    !editCat || c.productCategoryId !== editCat.productCategoryId
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Product Categories (Admin)</h1>
            <p className="text-muted-foreground mt-1">Manage product category hierarchy across all organizations</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchCategories}>
              <RefreshCw className="h-4 w-4 mr-2" />Refresh
            </Button>
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-2" />Add Category
            </Button>
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle>Categories</CardTitle>
            <CardDescription>{categories.length} total categories</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : categories.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Tag className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No categories found.</p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Parent</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {flattenCategories(categories).map(cat => (
                      <TableRow key={cat.productCategoryId}>
                        <TableCell className="font-medium">
                          {cat.depth > 0 && (
                            <span className="text-muted-foreground mr-1">
                              {'—'.repeat(cat.depth)}{' '}
                            </span>
                          )}
                          {cat.name}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{cat.parentCategoryName || '—'}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{cat.description || '—'}</TableCell>
                        <TableCell>
                          <Badge variant={cat.isActive ? 'success' : 'secondary'}>
                            {cat.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(cat)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(cat)}
                              className="text-destructive hover:text-destructive">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editCat ? 'Edit Category' : 'Add Category'}</DialogTitle>
            <DialogDescription>
              {editCat ? 'Update category details' : 'Create a new product category. Select a parent to nest it under another category.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Category Name *</Label>
              <Input
                placeholder="e.g. Flower, Concentrates"
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Parent Category</Label>
              <Select
                value={form.parentCategoryId || 'none'}
                onValueChange={v => setForm(p => ({ ...p, parentCategoryId: v === 'none' ? '' : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No parent (root category)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No parent (root) —</SelectItem>
                  {flatForSelect.map(cat => (
                    <SelectItem key={cat.productCategoryId} value={String(cat.productCategoryId)}>
                      {'  '.repeat(cat.depth)}{cat.depth > 0 ? '↳ ' : ''}{cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Input
                placeholder="Optional description"
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              />
            </div>
            {editCat && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={form.isActive}
                  onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))}
                />
                <Label htmlFor="isActive">Active</Label>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button className="flex-1" onClick={handleSave} disabled={isSaving}>
                {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                {editCat ? 'Save Changes' : 'Create Category'}
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setIsFormOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
